import type { CSSProperties, ReactNode } from "react";

type RuntimeBackgroundImageAttributes = { style: CSSProperties };

interface RuntimeBackgroundImageLayerProps {
  imageUrl: string;
  overlay?: "darkScrim";
  children: (attributes: RuntimeBackgroundImageAttributes) => ReactNode;
}

type RuntimeBackgroundImagePresentation =
  | "thumbnail"
  | "attachmentIcon"
  | "cardHeader"
  | "cardAside"
  | "banner"
  | "fill"
  | "transparentFill";

interface RuntimeBackgroundImageProps {
  imageUrl?: string;
  children?: ReactNode;
  presentation?: RuntimeBackgroundImagePresentation;
  "aria-description"?: string;
}

const presentationStyles: Record<RuntimeBackgroundImagePresentation, string> = {
  thumbnail:
    "mx-auto flex h-card-media w-full shrink-0 items-center justify-center overflow-hidden rounded-xs bg-center",
  attachmentIcon: "size-icon-display shrink-0 bg-contain bg-center bg-no-repeat",
  cardHeader:
    "flex h-card-media w-full items-center justify-center overflow-hidden rounded-t-xl bg-background-tertiary bg-cover bg-center p-space-sm",
  cardAside: "relative size-full rounded-r-xl bg-background-tertiary bg-cover bg-center",
  banner: "h-card-media w-full bg-background-tertiary bg-cover bg-center",
  fill: "size-full bg-background-brand-secondary bg-cover bg-center",
  transparentFill: "size-full bg-cover bg-center",
};

const overlayStyles = {
  darkScrim: "linear-gradient(to bottom, rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0.8))",
} as const;

function cssUrl(imageUrl: string): string {
  return `url(${JSON.stringify(imageUrl)})`;
}

export function RuntimeBackgroundImage({
  imageUrl,
  children,
  presentation = "thumbnail",
  "aria-description": ariaDescription,
}: RuntimeBackgroundImageProps): ReactNode {
  return (
    <div
      aria-description={ariaDescription}
      className={presentationStyles[presentation]}
      style={{ backgroundImage: imageUrl ? cssUrl(imageUrl) : undefined }}
    >
      {children}
    </div>
  );
}

/** Applies a runtime image URL while leaving product-owned surface presentation at the call site. */
export function RuntimeBackgroundImageLayer({
  imageUrl,
  overlay,
  children,
}: RuntimeBackgroundImageLayerProps): ReactNode {
  const image = cssUrl(imageUrl);
  const backgroundImage = overlay ? `${overlayStyles[overlay]}, ${image}` : image;

  return children({ style: { backgroundImage } });
}
